import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';
import dayjs from 'dayjs';
import { SUPPORTED_LANGUAGES, LANGUAGE_STORAGE_KEY, type LanguageCode } from '../i18n/languages';
import type { AppI18n } from '../i18n/config';

// Static, per-language dayjs locale loaders. The import specifiers must be string
// literals (not a template like `dayjs/locale/${x}.js`) so the bundler can analyze and
// split them; a variable specifier ships as a bare module specifier the browser cannot
// resolve. Keyed by LanguageCode; the value matches each entry's `dayjs` locale key.
// Partial on purpose: a language present in the registry without a dayjs loader simply
// keeps the previous dayjs locale (date formatting degrades, the UI still translates).
const dayjsLocaleLoaders: Partial<Record<LanguageCode, () => Promise<unknown>>> = {
  en: () => import('dayjs/locale/en.js'),
  es: () => import('dayjs/locale/es.js'),
  fr: () => import('dayjs/locale/fr.js'),
  de: () => import('dayjs/locale/de.js'),
  ja: () => import('dayjs/locale/ja.js'),
  pt: () => import('dayjs/locale/pt.js'),
  'pt-BR': () => import('dayjs/locale/pt-br.js'),
  ar: () => import('dayjs/locale/ar.js'),
  cs: () => import('dayjs/locale/cs.js'),
  da: () => import('dayjs/locale/da.js'),
  it: () => import('dayjs/locale/it.js'),
  ko: () => import('dayjs/locale/ko.js'),
  nl: () => import('dayjs/locale/nl.js'),
  pl: () => import('dayjs/locale/pl.js'),
  sv: () => import('dayjs/locale/sv.js'),
};

const I18nContext = createContext<
  | {
      language: LanguageCode;
      setLanguage: (code: LanguageCode) => void;
      // The languages this app ships (from the i18n controller). Consumers like the
      // LanguageSwitcher use this to offer only loadable languages.
      availableLanguages: LanguageCode[];
    }
  | undefined
>(undefined);

export const useLanguage = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useLanguage must be used within I18nProvider');
  return ctx;
};

// Non-throwing variant for consumers that can function without an I18nProvider (e.g.
// ThemeProvider, which only needs the language to localize AntD's built-in component
// strings and falls back to the default language when no provider is present). Returns
// undefined instead of throwing so the theme can be used standalone in apps that have
// not adopted i18n yet.
export const useLanguageOptional = () => useContext(I18nContext);

interface I18nProviderProps {
  // DI of an already-created i18n controller (see createI18n). Keeping the provider
  // app-agnostic mirrors how ThemeProvider stays free of app-specific config.
  i18n: AppI18n;
  children: ReactNode;
  // Defaults to true; set false to drive the language without touching localStorage.
  persist?: boolean;
}

export const I18nProvider = ({ i18n, children, persist }: I18nProviderProps) => {
  const { instance, initialLanguage, availableLanguages, ensureLanguage } = i18n;
  // The instance boots on the eagerly-bundled default language, so seed state from it.
  const [language, setLanguageState] = useState<LanguageCode>(() => instance.language as LanguageCode);

  const setLanguage = (code: LanguageCode) => {
    // Make sure the target language's bundle is registered before switching, so the UI
    // never renders raw keys mid-load.
    void ensureLanguage(code).then(() => {
      void instance.changeLanguage(code);
      setLanguageState(code);
      if (persist !== false) {
        try {
          localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
        } catch {
          // localStorage can throw in private mode or with strict cookie settings.
        }
      }
    });
  };

  // Apply the detected/stored initial language once on mount. The instance starts on the
  // eager default, so a different stored preference is loaded and switched in here (brief
  // default-language flash until its bundle resolves). Persists nothing: it is already
  // the stored value.
  useEffect(() => {
    if (initialLanguage === instance.language) return;
    void ensureLanguage(initialLanguage).then(() => {
      void instance.changeLanguage(initialLanguage);
      setLanguageState(initialLanguage);
    });
  }, [initialLanguage, instance, ensureLanguage]);

  // Cross-cutting sync that the rest of the app depends on: keep <html lang>/<html dir>
  // current and lazy-load the matching dayjs locale so date formatting follows the
  // language. `dir` flips the document writing direction for RTL languages (Arabic);
  // AntD components mirror separately via ConfigProvider (see ThemeContext).
  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
    const meta = SUPPORTED_LANGUAGES.find((l) => l.code === language);
    if (!meta) return;
    document.documentElement.setAttribute('dir', meta.dir);
    const dayjsLoader = dayjsLocaleLoaders[language];
    if (!dayjsLoader) return;
    void dayjsLoader().then(() => {
      dayjs.locale(meta.dayjs);
    });
  }, [language]);

  return (
    <I18nContext.Provider value={{ language, setLanguage, availableLanguages }}>
      <I18nextProvider i18n={instance}>{children}</I18nextProvider>
    </I18nContext.Provider>
  );
};
